import React, { useEffect, useRef, useState } from "react";
import { Brain, X, Check } from "lucide-react";

/**
 * GlobalContextPanel — the whole call context the agent is carrying, in one
 * place, opened from the left panel's header.
 *
 * Why it exists: the context blocks live in the Copilot thread, in the order
 * they were attached, and scroll away. What the server actually holds is the
 * JOIN of all of them (session.call_context, prefixed onto every turn), and
 * that joined text was previously unreadable — the engineer could edit a block
 * from twenty messages ago only by scrolling back to find it, and could never
 * see the sum. This panel is that sum, reachable at any time.
 *
 * One view: the joined text as a single editable field. Saving it MERGES — the
 * block list collapses to one block holding the edited text. That is the honest
 * reading of "I rewrote the whole thing": the old boundaries are gone, so they
 * stop being shown as separate, editable blocks. New call notes attach after it
 * as usual. Per-block editing still lives on the thread's own CallContextCard;
 * this panel deliberately shows only the sum, so there is no view to switch to
 * and no tab bar.
 *
 * Nothing here asks the agent anything. Saving updates what the agent will be
 * told from the next turn on — same contract as the in-thread card.
 */
const GlobalContextPanel = ({ open, blocks = [], onClose, onReplaceAll }) => {
  const [draft, setDraft] = useState("");
  // The field follows the blocks (a new call note landing under it, the panel
  // reopening) until the engineer starts typing — then their text wins until
  // they save or cancel.
  const [dirty, setDirty] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const textareaRef = useRef(null);

  const joined = blocks.map((b) => b.text).filter(Boolean).join("\n\n");

  useEffect(() => { if (!dirty) setDraft(joined); }, [joined, dirty]);

  useEffect(() => { if (!open) { setDirty(false); setSavedFlash(false); } }, [open]);

  if (!open) return null;

  const reset = () => { setDraft(joined); setDirty(false); };

  const commitFull = () => {
    const next = draft.trim();
    if (!next || next === joined.trim()) { reset(); return; }
    onReplaceAll?.(next);
    setDirty(false);
    setSavedFlash(true);
    window.setTimeout(() => setSavedFlash(false), 2000);
  };

  return (
    <div className="d5-gctx" role="dialog" aria-label="Call / Chat context">
      <div className="d5-gctx-head">
        <span className="d5-gctx-title">
          <Brain size={11} /> Call / Chat context
          <span className="d5-gctx-count">
            {blocks.length ? `${blocks.length} block${blocks.length > 1 ? "s" : ""} · ${joined.length} chars` : "nothing attached yet"}
          </span>
          {savedFlash && <span className="d5-gctx-flash"><Check size={11} /> Merged into one block</span>}
        </span>
        <button type="button" className="d5-gctx-x" title="Close" onClick={onClose}>
          <X size={14} />
        </button>
      </div>

      <div className="d5-gctx-body">
        {blocks.length === 0 && (
          <p className="d5-gctx-empty">
            Nothing attached. Press send in this panel during a call to hand the
            Copilot what has been said so far.
          </p>
        )}

        {blocks.length > 0 && (
          <>
            <p className="d5-gctx-note">
              This is exactly what the agent is told before every question.
              Saving replaces all {blocks.length} block{blocks.length > 1 ? "s" : ""} with this one text.
            </p>
            <textarea
              ref={textareaRef}
              className="d5-gctx-edit"
              value={draft}
              onChange={(e) => { setDraft(e.target.value); setDirty(true); }}
              // Cmd/Ctrl+Enter saves; a plain Enter is a newline, because this
              // field is paragraphs of call notes, not a chat line.
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); commitFull(); }
                if (e.key === "Escape") reset();
              }}
            />
            <div className="d5-gctx-actions">
              <button type="button" className="d5-gctx-cancel" onClick={reset}>
                Cancel
              </button>
              <button type="button" className="d5-gctx-save" onClick={commitFull}>
                <Check size={12} /> Save context
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default GlobalContextPanel;
